import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useState } from 'react';
import { useLoader } from '../../../../../helpers/useLoader';
import { ToolDeleteStrategy } from '../../../projectDetails.models';
import { AnnotationTypeDelete } from '../components/AnnotationTypeDelete';
import { IProjectDetailsToolsBl } from '../projectDetailsTools.bl';
import { IAnnotationType } from '../projectDetailsTools.models';

interface IProps {
  isUsedAsScope: boolean;
  annotationType: IAnnotationType;
}

export const AnnotationTypeDeleteContainer = observer((props: IProps): ReactElement => {
  const projectDetailsToolsBl = useInjection(IProjectDetailsToolsBl.$);

  const { isLoading, withLoaderAsync } = useLoader();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [showDiscardConfirmModal, setShowDiscardConfirmModal] = useState<boolean>(false);
  const [strategy, setStrategy] = useState<ToolDeleteStrategy>(ToolDeleteStrategy.None);

  const handleDeleteConfirmed = async (): Promise<void> => {
    if (strategy === ToolDeleteStrategy.Discard) {
      await withLoaderAsync(async (): Promise<void> => {
        await projectDetailsToolsBl.getAnnotationTypeAffectAsync(props.annotationType.id);
        setShowDiscardConfirmModal(true);
      }, 'tool-delete-confirm-button');
    } else {
      await handleDeleteAnnotationTypeAsync();
    }
  };

  const handleDeleteAnnotationTypeAsync = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      await projectDetailsToolsBl.deleteAnnotationTypeAsync(props.annotationType.id, strategy);
      setShowDiscardConfirmModal(false);
    }, 'tool-delete-confirm-button');
  };

  const handleCancel = (): void => {
    if (!isLoading) {
      setShowModal(false);
    }
  };

  const handleCancelDiscard = (): void => {
    if (!isLoading) {
      setShowDiscardConfirmModal(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    await withLoaderAsync(async (): Promise<void> => {
      await projectDetailsToolsBl.getAnnotationTypeUsageAsync(props.annotationType.id);
      setShowModal(true);
    });
  };

  const handleStrategyChange = (strategy: ToolDeleteStrategy): void => {
    setStrategy(strategy);
  };

  const toolUsage = projectDetailsToolsBl.store.toolsUsage;
  const toolAffect = projectDetailsToolsBl.store.toolAffect;

  return (
    <AnnotationTypeDelete
      isLoading={isLoading}
      showModal={showModal}
      isUsedAsScope={props.isUsedAsScope}
      showDiscardConfirmModal={showDiscardConfirmModal}
      strategy={strategy}
      toolUsage={toolUsage}
      onCancel={handleCancel}
      onCancelDiscard={handleCancelDiscard}
      onDelete={handleDelete}
      onConfirmDiscard={handleDeleteAnnotationTypeAsync}
      onDeleteConfirmed={handleDeleteConfirmed}
      onStrategyChange={handleStrategyChange}
      toolAffect={toolAffect}
    />
  );
});
